import { Link, useParams } from 'react-router-dom';
import { Card, Title, Text, Badge, Metric, Flex, Divider } from '@tremor/react';
import { useApi } from './useApi';
import { api } from '../api';
import { useState } from 'react';

type ResponseDetail = {
  response: {
    response_id: string;
    recipient_id: string;
    member_id: string;
    survey_key: string;
    survey_version: number;
    dock_name: string | null;
    completed_at: string | null;
    started_at: string;
    nps_score: number | null;
    csat_score: number | null;
    ces_score: number | null;
    sentiment: string | null;
    sentiment_score: number | null;
    themes: string[] | null;
    flagged: boolean;
    flag_reason: string | null;
    first_name: string | null;
    last_name: string | null;
  };
  answers: Array<{ question_key: string; value_json: unknown; value_text: string | null }>;
  member_history: Array<{ completed_at: string; survey_key: string; nps_score: number | null }>;
};

export function ResponseDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [bumpKey, setBumpKey] = useState(0); // re-fetch after toggling flag
  const data = useApi<ResponseDetail>(`/api/admin/metrics/responses/${id}`, [id, bumpKey]);

  const onToggleFlag = async (currentlyFlagged: boolean): Promise<void> => {
    if (!id) return;
    await api(`/api/admin/reviewer/responses/${id}/flag`, {
      method: 'POST',
      body: JSON.stringify({ flagged: !currentlyFlagged }),
    });
    setBumpKey((k) => k + 1);
  };

  return (
    <div>
      <Link to="/admin/responses" className="text-sm text-blue-600 hover:underline">← All responses</Link>

      {data.kind === 'loading' && <Text>Loading…</Text>}
      {data.kind === 'error' && <Text>Couldn't load response ({data.message})</Text>}
      {data.kind === 'ready' && (
        <>
          <Flex alignItems="center" justifyContent="between" className="mt-4 mb-4">
            <Title>{(data.data.response.first_name ?? '') + ' ' + (data.data.response.last_name ?? '')} · {surveyLabel(data.data.response.survey_key)}</Title>
            <button
              type="button"
              onClick={() => void onToggleFlag(data.data.response.flagged)}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 hover:bg-gray-50"
            >
              {data.data.response.flagged ? 'Unflag' : 'Flag for review'}
            </button>
          </Flex>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <Text>NPS</Text>
              <Metric>{data.data.response.nps_score ?? '—'}</Metric>
            </Card>
            <Card>
              <Text>Sentiment</Text>
              <Metric>{data.data.response.sentiment ?? '—'}</Metric>
              <Text className="mt-2">{data.data.response.sentiment_score?.toFixed(2) ?? ''}</Text>
            </Card>
            <Card>
              <Text>Dock · Completed</Text>
              <Metric className="text-base mt-1">{data.data.response.dock_name ?? '—'}</Metric>
              <Text className="mt-2">{formatDate(data.data.response.completed_at)}</Text>
            </Card>
          </div>

          {data.data.response.flagged && (
            <Card className="mb-6">
              <Flex alignItems="center" justifyContent="start" className="gap-3">
                <Badge color="red">Flagged · {data.data.response.flag_reason ?? 'unknown'}</Badge>
                <Text>Phase 6 attaches an AI-drafted reply here.</Text>
              </Flex>
            </Card>
          )}

          <Card className="mb-6">
            <Title>Answers</Title>
            <Divider />
            <div className="space-y-3">
              {data.data.answers.map((a) => (
                <div key={a.question_key}>
                  <div className="text-xs uppercase text-gray-500 tracking-wide">{a.question_key}</div>
                  <div className="mt-1">{formatAnswer(a.value_json, a.value_text)}</div>
                </div>
              ))}
              {data.data.answers.length === 0 && <Text>No answer rows.</Text>}
            </div>
          </Card>

          <Card>
            <Title>Member NPS history</Title>
            <Divider />
            {data.data.member_history.length === 0 ? (
              <Text>No prior surveys for this member.</Text>
            ) : (
              <div className="space-y-2">
                {data.data.member_history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{surveyLabel(h.survey_key)}</span>
                    <span className="text-gray-500">{formatDate(h.completed_at)}</span>
                    <span className="font-medium">{h.nps_score ?? '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function surveyLabel(key: string): string {
  return ({ post_sale: 'Post-sale', post_trip: 'Post-trip', post_training: 'Post-training' } as Record<string, string>)[key] ?? key;
}
function formatAnswer(json: unknown, text: string | null): string {
  if (text) return text;
  if (json === null || json === undefined) return '—';
  if (typeof json === 'string') return json;
  if (typeof json === 'number' || typeof json === 'boolean') return String(json);
  if (Array.isArray(json)) return json.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ');
  return JSON.stringify(json, null, 2);
}
